import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import { api } from './client';
import type {
  Action,
  ActionResult,
  Commande,
  CommandeDetail,
  CommandeSummary,
  CreateCommandeInput,
  CreateCommandeResult,
  JournalEntry,
  JournalFilters,
  Lieu,
  Link,
  Manifestation,
  Scenario,
  Seance,
  Settings,
  SettingsUpdate,
} from './types';

export const qk = {
  settings: ['settings'] as const,
  commandes: ['commandes'] as const,
  commande: (guid: string) => ['commande', guid] as const,
  journal: (filters: JournalFilters) => ['journal', filters] as const,
  scenarios: ['scenarios'] as const,
  lieux: ['lieux'] as const,
  manifestations: ['manifestations'] as const,
  seances: ['seances'] as const,
};

/* ------------------------------------------------------------------ settings */

export function useSettings(options?: Partial<UseQueryOptions<Settings>>) {
  return useQuery<Settings>({
    queryKey: qk.settings,
    queryFn: () => api.get<Settings>('/settings'),
    ...options,
  });
}

export function useUpdateSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SettingsUpdate) => api.put<Settings>('/settings', body),
    onSuccess: (data) => {
      qc.setQueryData(qk.settings, data);
    },
  });
}

/* ------------------------------------------------------------------ commandes */

export function useCommandes(limit = 50) {
  return useQuery({
    queryKey: [...qk.commandes, limit],
    queryFn: () => api.get<{ items: CommandeSummary[] }>('/commandes', { limit }),
    select: (data) => data.items,
  });
}

export function useCommande(guid: string | undefined) {
  return useQuery({
    queryKey: qk.commande(guid ?? ''),
    queryFn: () => api.get<CommandeDetail>(`/commandes/${guid}`),
    enabled: Boolean(guid),
  });
}

export function useCreateCommande() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateCommandeInput) => api.post<CreateCommandeResult>('/commandes', body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.commandes });
    },
  });
}

export function useDeleteCommande() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (guid: string) => api.delete<void>(`/commandes/${guid}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.commandes });
    },
  });
}

export function useRegenerateLink(guid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<Link>(`/commandes/${guid}/links`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.commande(guid) });
    },
  });
}

/** Runs any §10.2 action on an order; the union keeps body and action name in sync. */
export function useRunAction(guid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Action) =>
      api.post<ActionResult>(`/commandes/${guid}/actions/${input.action}`, input.body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.commande(guid) });
      void qc.invalidateQueries({ queryKey: qk.commandes });
    },
  });
}

/* ------------------------------------------------------------------ journal */

export function useJournal(filters: JournalFilters = {}, options?: { refetchInterval?: number }) {
  const { status: _status, ...serverFilters } = filters;
  return useQuery({
    queryKey: qk.journal(filters),
    queryFn: () =>
      api.get<{ items: JournalEntry[] }>('/journal', {
        guid: serverFilters.guid,
        endpoint: serverFilters.endpoint,
        limit: serverFilters.limit ?? 100,
        before: serverFilters.before,
      }),
    select: (data) =>
      filters.status
        ? data.items.filter((entry) => String(entry.status).startsWith(filters.status as string))
        : data.items,
    refetchInterval: options?.refetchInterval ?? 2000,
  });
}

/* ------------------------------------------------------------------ scenarios */

export function useScenarios() {
  return useQuery({
    queryKey: qk.scenarios,
    queryFn: () => api.get<{ items: Scenario[] }>('/scenarios'),
    select: (data) => data.items,
  });
}

export function useRunScenario() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => api.post<CreateCommandeResult>(`/scenarios/${name}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.commandes });
    },
  });
}

/* ------------------------------------------------------------------ référentiel */

function crudHooks<T, K extends string | number>(
  path: string,
  key: readonly unknown[],
  idOf: (item: T) => K,
) {
  const useList = () =>
    useQuery({
      queryKey: key,
      queryFn: () => api.get<{ items: T[] }>(path),
      select: (data) => data.items,
    });

  const useCreate = () => {
    const qc = useQueryClient();
    return useMutation({
      mutationFn: (body: Partial<T>) => api.post<T>(path, body),
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: key });
      },
    });
  };

  const useUpdate = () => {
    const qc = useQueryClient();
    return useMutation({
      mutationFn: (item: T) => api.put<T>(`${path}/${idOf(item)}`, item),
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: key });
      },
    });
  };

  const useDelete = () => {
    const qc = useQueryClient();
    return useMutation({
      mutationFn: (id: K) => api.delete<void>(`${path}/${id}`),
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: key });
      },
    });
  };

  return { useList, useCreate, useUpdate, useDelete };
}

const lieux = crudHooks<Lieu, number>('/lieux', qk.lieux, (l) => l.idLieu);
const manifestations = crudHooks<Manifestation, number>(
  '/manifestations',
  qk.manifestations,
  (m) => m.idManif,
);
const seances = crudHooks<Seance, number>('/seances', qk.seances, (s) => s.idSeance);

export const useLieux = lieux.useList;
export const useCreateLieu = lieux.useCreate;
export const useUpdateLieu = lieux.useUpdate;
export const useDeleteLieu = lieux.useDelete;

export const useManifestations = manifestations.useList;
export const useCreateManifestation = manifestations.useCreate;
export const useUpdateManifestation = manifestations.useUpdate;
export const useDeleteManifestation = manifestations.useDelete;

export const useSeances = seances.useList;
export const useCreateSeance = seances.useCreate;
export const useUpdateSeance = seances.useUpdate;
export const useDeleteSeance = seances.useDelete;

export type { Commande };
